import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';

import { InputTextModule } from 'primeng/inputtext';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AlertsComponent,
  AlertsService,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengPasswordDirective,
} from '@web/shared/ui';
import { Button } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorService } from '@web/common';
import { HttpErrorResponse } from '@angular/common/http';
import { PublicBoxAuthService } from '../../services/public-box-auth.service';

type PublicBoxDetailsPasswordFormGroup = FormGroup<{
  password: FormControl<string>;
}>;

@Component({
  selector: 'app-public-box-details-authorization',
  imports: [
    InputTextModule,
    ReactiveFormsModule,
    FormControlComponent,
    Button,
    PrimengButtonDirective,
    PasswordModule,
    PrimengPasswordDirective,
    AlertsComponent,
  ],
  providers: [AlertsService],
  templateUrl: './public-box-details-authorization.component.html',
  styleUrl: './public-box-details-authorization.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicBoxDetailsAuthorizationComponent {
  private readonly builder = inject(FormBuilder);
  private readonly publicBoxAuthorization = inject(PublicBoxAuthService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public readonly slug = input.required<string>();
  public readonly authorizationSuccessEvent = output();

  protected readonly form = this.buildForm();

  private readonly destroyRef = inject(DestroyRef);

  public handleSubmitClick(): void {
    const { password } = this.form.getRawValue();

    this.publicBoxAuthorization
      .authorize(this.slug(), password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.authorizationSuccessEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private buildForm(): PublicBoxDetailsPasswordFormGroup {
    return this.builder.group({
      password: this.builder.nonNullable.control('', [Validators.required]),
    });
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    this.alerts.showSingle((builder) =>
      builder.buildFailure(
        this.httpError.extract(response).multiLineMessages(),
      ),
    );
  }
}
